// Chasis del panel de detalle de un elemento con ciclo de vida: encabezado (nombre, estado, id,
// acciones), avisos, cuerpo con los campos del dominio, historial y la barra de guardar.
import type { ReactNode } from 'react';
import { CopyableId } from '@/kit';
import { cn } from '@/lib/cn';
import { SaveBar, type SaveBarProps } from '@/shared/patterns/SaveBar';
import { LifecycleStatusBadge } from './LifecycleBadges';
import type { LifecycleStatus } from './lifecycleStatus';

export interface LifecycleEditorShellProps {
    /** Nombre accesible del formulario. */
    ariaLabel: string;
    title: ReactNode;
    /** Título apagado: el elemento todavía no tiene nombre. */
    untitled?: boolean;
    /** Hay cambios sin guardar; el título lleva un punto delante. */
    dirty: boolean;
    subtitle?: ReactNode;
    /** `null` mientras se redacta uno nuevo. */
    status: LifecycleStatus | null;
    statusTitle?: string;
    /** Chips propios del dominio que van antes del estado: tipo, «Referido», «Sistema». */
    tags?: ReactNode;
    id?: { label: string; value: string };
    /** Normalmente `<LifecycleActions>`. */
    actions?: ReactNode;
    /** Franjas bajo el encabezado: no editable, versión nueva, cambios sin publicar. */
    notices?: ReactNode;
    children: ReactNode;
    history?: ReactNode;
    /** Va entre el cuerpo y la barra de guardar: los problemas que impiden guardar. */
    footer?: ReactNode;
    saveBar: SaveBarProps;
}

export function LifecycleEditorShell({
    ariaLabel,
    title,
    untitled = false,
    dirty,
    subtitle,
    status,
    statusTitle,
    tags,
    id,
    actions,
    notices,
    children,
    history,
    footer,
    saveBar,
}: LifecycleEditorShellProps) {
    return (
        // Sin <form>: el historial trae sus propios botones y no debe enviar nada.
        <div role="form" aria-label={ariaLabel} className="flex min-h-0 flex-1 flex-col">
            <header className="flex flex-none items-start justify-between gap-3 border-b border-border bg-card px-4 py-3">
                <div className="min-w-0 flex-1">
                    <h2
                        className={cn(
                            'truncate text-[15px] font-bold tracking-[-0.01em] text-foreground',
                            untitled && 'text-muted-foreground',
                        )}
                    >
                        {dirty ? '• ' : ''}
                        {title}
                    </h2>
                    {subtitle || id ? (
                        <div className="mt-[3px] flex min-w-0 items-center gap-1 text-[10.5px] text-muted-foreground">
                            {subtitle ? (
                                <span className="flex-none truncate">{subtitle}</span>
                            ) : null}
                            {subtitle && id ? <span aria-hidden>·</span> : null}
                            {id ? (
                                <CopyableId
                                    inline
                                    label={id.label}
                                    value={id.value}
                                    className="min-w-0"
                                />
                            ) : null}
                        </div>
                    ) : null}
                </div>
                <div className="flex flex-none items-center gap-1.5">
                    {tags}
                    {status ? <LifecycleStatusBadge status={status} title={statusTitle} /> : null}
                    {actions}
                </div>
            </header>

            {notices}

            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-3.5">
                {children}
                {history}
            </div>

            {footer}
            <SaveBar {...saveBar} />
        </div>
    );
}

export type LifecycleNoticeTone = 'muted' | 'info' | 'warning';

const NOTICE_TONE_CLASS: Record<LifecycleNoticeTone, string> = {
    muted: 'border-border bg-muted/30 text-muted-foreground',
    info: 'border-info/30 bg-info/10 text-info',
    warning: 'border-warning/40 bg-warning/10 text-foreground',
};

/** Franja de aviso bajo el encabezado, con una acción opcional a la derecha. */
export function LifecycleNotice({
    tone = 'muted',
    role = 'status',
    action,
    children,
}: {
    tone?: LifecycleNoticeTone;
    role?: 'status' | 'note' | 'alert';
    action?: ReactNode;
    children: ReactNode;
}) {
    return (
        <div
            role={role}
            className={cn(
                'flex flex-none items-center gap-2 border-b px-4 py-1.5 text-[10.5px]',
                NOTICE_TONE_CLASS[tone],
            )}
        >
            <span className="min-w-0 flex-1">{children}</span>
            {action}
        </div>
    );
}
